import React from 'react';
import { Logo } from '@dopomogai/ui';

type TierKey = 'trainee' | 'junior' | 'middle' | 'senior';

type Props = {
  tier: TierKey;
  size?: 'sm' | 'md';
  className?: string;
  showLogo?: boolean;
  ariaLabel?: string;
};

const LABEL: Record<TierKey, string> = {
  trainee: 'Trainee',
  junior: 'Junior',
  middle: 'Middle',
  senior: 'Senior',
};

function colorClasses(tier: TierKey) {
  switch (tier) {
    case 'senior':
      return {
        bg: 'bg-violet-100',
        text: 'text-violet-900',
        dot: 'bg-violet-700',
        ring: 'ring-violet-200',
      };
    case 'middle':
      return {
        bg: 'bg-sky-100',
        text: 'text-sky-900',
        dot: 'bg-sky-700',
        ring: 'ring-sky-200',
      };
    case 'junior':
      return {
        bg: 'bg-emerald-100',
        text: 'text-emerald-900',
        dot: 'bg-emerald-700',
        ring: 'ring-emerald-200',
      };
    case 'trainee':
    default:
      return {
        bg: 'bg-slate-100',
        text: 'text-slate-900',
        dot: 'bg-slate-700',
        ring: 'ring-slate-200',
      };
  }
}

export const TierBadge: React.FC<Props> = ({
  tier,
  size = 'sm',
  className,
  showLogo = true,
  ariaLabel,
}) => {
  const c = colorClasses(tier);
  const pad = size === 'md' ? 'px-2.5 py-1' : 'px-2 py-0.5';
  const textSize = size === 'md' ? 'text-xs' : 'text-[11px]';

  return (
    <span
      className={[
        'inline-flex items-center gap-1.5 rounded-full border-2 border-black',
        c.bg,
        c.text,
        pad,
        'ring-1',
        c.ring,
        'shadow-[2px_2px_0_0_rgb(17,24,39)]',
        'select-none',
        className || '',
      ].join(' ')}
      aria-label={ariaLabel || `${LABEL[tier]} badge`}
    >
      {showLogo ? (
        <span className="inline-flex items-center justify-center -ml-0.5">
          <Logo logoType="favicon" size="sm" className="w-3.5 h-3.5" />
        </span>
      ) : (
        <span className={['inline-block w-2 h-2 rounded-full', c.dot].join(' ')} />
      )}
      <span className={['font-extrabold uppercase tracking-wide', textSize].join(' ')}>
        {LABEL[tier]}
      </span>
    </span>
  );
};

export default TierBadge;
